// -----------------------------------------------------------------------------
// shared/events.ts — a tiny, type-safe event emitter.
//
// Used where a plain callback isn't enough — e.g. the player broadcasting
// time/play/pause/ended, and the exporter broadcasting progress — so several UI
// pieces can listen without the engine knowing who they are. The render engine
// stays imperative; this is just a notification channel, never a render path.
// -----------------------------------------------------------------------------

export type Handler<T> = (payload: T) => void;

export class Emitter<Events> {
  private handlers = new Map<keyof Events, Set<Handler<unknown>>>();

  on<K extends keyof Events>(type: K, handler: Handler<Events[K]>): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    set.add(handler as Handler<unknown>);
    return () => this.off(type, handler);
  }

  off<K extends keyof Events>(type: K, handler: Handler<Events[K]>): void {
    this.handlers.get(type)?.delete(handler as Handler<unknown>);
  }

  emit<K extends keyof Events>(type: K, payload: Events[K]): void {
    const set = this.handlers.get(type);
    if (!set) return;
    // Copy so handlers that unsubscribe mid-emit don't disturb iteration.
    for (const h of [...set]) (h as Handler<Events[K]>)(payload);
  }
}
